import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

const Step2 = ({
  // State values
  firstName,
  lastName,
  email,

  // Error states (using your centralized errors object)
  errors,

  // Handler functions
  handleFirstNameChange,
  handleLastNameChange,
  clearFieldError,
  hasTitle,

  // Display control
  isVisible = true,
  stepNumber = 2,
  totalSteps = 3,
  showNewPassword = false,
}) => {
  if (!isVisible) {
    return null;
  }

  const [newPasswordValue, setNewPasswordValue] = useState("");
  const [isPasswordRequired, setIsPasswordRequired] =
    useState(!showNewPassword);

  useEffect(() => {
    if (showNewPassword) {
      setIsPasswordRequired(newPasswordValue.trim().length > 0);
    }
  }, [newPasswordValue, showNewPassword]);

  const handleNewPasswordChange = e => {
    setNewPasswordValue(e.target.value);
    clearFieldError("new_password");
  };

  return (
    <fieldset
      className={`registration-form__step registration-form__step-2 ${!isVisible ? "registration-form__step--hidden" : ""}`}
      id="step-2"
      hidden={!isVisible}
      aria-describedby="step-2-description"
    >
      {hasTitle && (
        <legend className="registration-form__title">
          Step {stepNumber} of {totalSteps}: Account Information
        </legend>
      )}

      <p
        className="registration-form__description sr-only"
        id="step-2-description"
      >
        Please provide your personal account information to create your login
        credentials.
      </p>

      <div className="registration-form__field registration-form__field--required">
        <label htmlFor="first-name" className="registration-form__label">
          First Name{" "}
          <span className="registration-form__required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          type="text"
          id="first-name"
          name="first_name"
          className={`registration-form__input ${errors.first_name ? "registration-form__input--error" : ""}`}
          value={firstName}
          onChange={handleFirstNameChange}
          aria-invalid={errors.first_name ? "true" : "false"}
          required
        />
        <div
          id="first-name-error"
          className="registration-form__error"
          hidden={!errors.first_name}
        >
          {errors.first_name}
        </div>
      </div>

      <div className="registration-form__field registration-form__field--required">
        <label htmlFor="last-name" className="registration-form__label">
          Last Name{" "}
          <span className="registration-form__required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          type="text"
          id="last-name"
          name="last_name"
          className={`registration-form__input ${errors.last_name ? "registration-form__input--error" : ""}`}
          value={lastName}
          onChange={handleLastNameChange}
          aria-invalid={errors.last_name ? "true" : "false"}
          required
        />
        <div
          id="last-name-error"
          className="registration-form__error"
          hidden={!errors.last_name}
        >
          {errors.last_name}
        </div>
      </div>

      <div className="registration-form__field registration-form__field--required">
        <label htmlFor="user-name" className="registration-form__label">
          User Name{" "}
          <span className="registration-form__required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          type="text"
          id="user-name"
          name="user_name"
          className="registration-form__input"
          value={email}
          readOnly
          disabled
          required
        />
      </div>

      <div
        className={`registration-form__field ${isPasswordRequired ? "registration-form__field--required" : ""}`}
      >
        <label htmlFor="password" className="registration-form__label">
          {showNewPassword ? "Current Password" : "Password"}{" "}
          {isPasswordRequired && (
            <span className="registration-form__required" aria-hidden="true">
              *
            </span>
          )}
        </label>
        <input
          type="password"
          id="password"
          name="password"
          className={`registration-form__input ${errors.password ? "registration-form__input--error" : ""}`}
          aria-invalid={errors.password ? "true" : "false"}
          required={isPasswordRequired}
          onChange={() => clearFieldError("password")}
        />
        {!showNewPassword && (
          <div
            className={`registration-form__hint ${errors.password ? "registration-form__error" : ""}`}
          >
            {errors.password ||
              "The password must be at least 6 characters and have 3 of the following 4 character types present: Uppercase letters (A-Z), Lowercase letters (a-z), Numerals (0-9), and Punctuation (!@#$%^&*()-_=+[]{}|;:,.)."}
          </div>
        )}
        {showNewPassword && errors.password && (
          <div className="registration-form__error">{errors.password}</div>
        )}
      </div>

      {/* New Password - Only for Member Profile */}
      {showNewPassword && (
        <div className="registration-form__field">
          <label htmlFor="new-password" className="registration-form__label">
            New Password
          </label>
          <input
            type="password"
            id="new-password"
            name="new_password"
            className={`registration-form__input ${errors.new_password ? "registration-form__input--error" : ""}`}
            aria-invalid={errors.new_password ? "true" : "false"}
            required={false}
            value={newPasswordValue}
            onChange={handleNewPasswordChange}
          />
          <div
            className={`registration-form__hint ${errors.new_password ? "registration-form__error" : ""}`}
          >
            {errors.new_password ||
              "The password must be at least 6 characters and have 3 of the following 4 character types present: Uppercase letters (A-Z), Lowercase letters (a-z), Numerals (0-9), and Punctuation (!@#$%^&*()-_=+[]{}|;:,.)."}
          </div>
        </div>
      )}

      <div
        className={`registration-form__field ${isPasswordRequired ? "registration-form__field--required" : ""}`}
      >
        <label htmlFor="confirm-password" className="registration-form__label">
          {showNewPassword ? "Confirm New Password" : "Confirm Password"}{" "}
          {isPasswordRequired && (
            <span className="registration-form__required" aria-hidden="true">
              *
            </span>
          )}
        </label>
        <input
          type="password"
          id="confirm-password"
          name="confirm_password"
          className={`registration-form__input ${errors.confirm_password ? "registration-form__input--error" : ""}`}
          aria-invalid={errors.confirm_password ? "true" : "false"}
          required={isPasswordRequired}
          onChange={() => clearFieldError("confirm_password")}
        />
        <div
          id="confirm-password-error"
          className="registration-form__error"
          hidden={!errors.confirm_password}
        >
          {errors.confirm_password}
        </div>
      </div>

      <div className="registration-form__field registration-form__field--required">
        <label htmlFor="email-address" className="registration-form__label">
          Email Address{" "}
          <span className="registration-form__required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          type="email"
          id="email-address"
          name="email_address"
          className="registration-form__input"
          value={email || ""}
          readOnly
          required
        />
      </div>
    </fieldset>
  );
};

Step2.propTypes = {
  // State values
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,

  // Error states (using centralized errors object)
  errors: PropTypes.object,

  // Handler functions
  handleFirstNameChange: PropTypes.func.isRequired,
  handleLastNameChange: PropTypes.func.isRequired,
  clearFieldError: PropTypes.func.isRequired,
  hasTitle: PropTypes.bool,

  // Display control
  isVisible: PropTypes.bool,
  stepNumber: PropTypes.number,
  totalSteps: PropTypes.number,
  showNewPassword: PropTypes.bool,
};

export default Step2;
